import api from '../api/axios';

export type LevelInfo = {
  level: number;
  nextThreshold: number | null;
  progressPct: number; // 0..100
};

// Level system configuration from backend - Single Source of Truth
let thresholds: number[] = [10, 100, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
let titles: string[] = [
  'Applicant',
  'Junior Contributor',
  'Contributor',
  'Skilled Contributor',
  'Senior Contributor',
  'Distinguished Contributor',
  'Elite Contributor',
  'Premier Contributor',
  'Vanguard Contributor',
  'Luminary Contributor',
  'Legacy Member',
];

// Fetch level system from backend on module load
let isInitialized = false;
const initPromise = initializeLevelSystem();

async function initializeLevelSystem() {
  if (isInitialized) return;
  try {
    const response = await api.get('/auth/user/level-system');
    if (response.data?.thresholds && Array.isArray(response.data.thresholds)) {
      thresholds = response.data.thresholds;
    }
    if (response.data?.titles && Array.isArray(response.data.titles)) {
      titles = response.data.titles;
    }
    isInitialized = true;
  } catch (error) {
    console.warn('Failed to fetch level system from backend, using defaults:', error);
  }
}

// Ensure level system is initialized before use
export async function ensureLevelSystemReady() {
  await initPromise;
}

export function getLevel(points: number): LevelInfo {
  // Defensive check: ensure thresholds is an array
  if (!Array.isArray(thresholds) || thresholds.length === 0) {
    console.error('Level thresholds not initialized properly, using defaults');
    thresholds = [10, 100, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
  }
  
  let level = 0;
  for (const t of thresholds) {
    if (points >= t) level++;
    else break;
  }
  const nextThreshold = level < thresholds.length ? thresholds[level] : null;
  let progressPct = 100;
  if (nextThreshold !== null) {
    const prev = level > 0 ? thresholds[level - 1] : 0;
    const span = nextThreshold - prev;
    progressPct = Math.max(0, Math.min(100, Math.round(((points - prev) / span) * 100)));
  }
  return { level, nextThreshold, progressPct };
}

export function formatLevelBadge(level: number): string {
  // Defensive check: ensure titles is an array
  if (!Array.isArray(titles) || titles.length === 0) {
    console.error('Level titles not initialized properly, using defaults');
    titles = [
      'Applicant',
      'Junior Contributor',
      'Contributor',
      'Skilled Contributor',
      'Senior Contributor',
      'Distinguished Contributor',
      'Elite Contributor',
      'Premier Contributor',
      'Vanguard Contributor',
      'Luminary Contributor',
      'Legacy Member',
    ];
  }
  return titles[level] || 'Applicant';
}
